import { Flex, HStack, Stack, useToast } from "@chakra-ui/react";
import * as yup from "yup";
import apiClient from "services/ApiClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { generatePath } from "react-router";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { Company } from "models/company";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ProceedCancelButtons from "components/Button/ProceedCancelButtons";
import { FloatingField } from "design-system";
import { companyKeys } from "features/Company/hooks";

interface PeopleTeamSettingsFormValues {
  hrQuestionContactId?: string | null;
  defaultPeopleTeamContactId?: string | null;
}

const schema = yup.object({
  hrQuestionContactId: yup.string().nullable().notRequired(),
  defaultPeopleTeamContactId: yup.string().nullable().notRequired(),
});

interface PeopleTeamSettingsFormProps {
  company: Company;
  onClose: () => void;
}

export function PeopleTeamSettingsForm({ company, onClose }: PeopleTeamSettingsFormProps) {
  const queryClient = useQueryClient();
  const toast = useToast({ status: "success" });

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
  } = useForm<PeopleTeamSettingsFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      hrQuestionContactId: company?.hrQuestionContactId ?? null,
      defaultPeopleTeamContactId: company?.defaultPeopleTeamContactId ?? null,
    },
  });

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: (data: PeopleTeamSettingsFormValues) =>
      apiClient.put<Company>(generatePath(API_ROUTES.companies.show, { id: "current" }), {
        company: {
          hr_question_contact_id: data.hrQuestionContactId ?? null,
          default_people_team_contact_id: data.defaultPeopleTeamContactId ?? null,
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: companyKeys.current() });
      toast({ title: "Saved!" });
      onClose();
    },
    onError: () => {
      toast({ title: "Error", description: "There was an error, please try again.", status: "error" });
    },
  });

  return (
    <Flex direction="column">
      <Stack spacing={4}>
        <FloatingField name="hrQuestionContactId" errors={errors.hrQuestionContactId?.message}>
          <FloatingField.Label>HR Question Contact</FloatingField.Label>
          <FloatingField.UserSelect control={control} name="hrQuestionContactId" />
        </FloatingField>
        <FloatingField name="defaultPeopleTeamContactId" errors={errors.defaultPeopleTeamContactId?.message}>
          <FloatingField.Label>Default People Team Contact</FloatingField.Label>
          <FloatingField.UserSelect control={control} name="defaultPeopleTeamContactId" />
        </FloatingField>
      </Stack>
      <HStack mt={10} justify="end">
        <ProceedCancelButtons
          handleSubmit={handleSubmit((data) => onSubmit(data))}
          onClose={() => { reset(); onClose(); }}
          proceedText="Save"
          isLoading={isPending}
          proceedDisabled={!isDirty}
        />
      </HStack>
    </Flex>
  );
}
